<template>
  <section class="editor-block">
    <div class="block-head">
      <span class="eyebrow">Content</span>
      <strong>富文本正文</strong>
    </div>
    <el-form-item label="内容">
      <div ref="richEditorRef" class="rich-editor">
        <Toolbar class="editor-toolbar" :editor="editorRef" :defaultConfig="toolbarConfig" />
        <Editor
          class="editor-body"
          v-model="htmlModel"
          :defaultConfig="editorConfig"
          mode="default"
          @custom-alert="handleEditorAlert"
          @onChange="handleEditorChange"
          @onCreated="handleEditorCreated"
        />
      </div>
    </el-form-item>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import '@wangeditor/editor/dist/css/style.css';
import { uploadImgBlob } from '@/api';
import { normalizeUploadAssetHtml, normalizeUploadAssetUrl } from './asset-url';

const props = defineProps<{
  html: string;
}>();

const emit = defineEmits<{
  (e: 'update:html', value: string): void;
  (e: 'update:previewHtml', value: string): void;
}>();

const richEditorRef = ref<HTMLElement>();
const editorRef = shallowRef();
const editorImageUploading = ref(false);
let imageResizeObserver: MutationObserver | null = null;
let previewSyncFrame = 0;

const htmlModel = computed({
  get: () => props.html,
  set: (value: string) => emit('update:html', value)
});

const toolbarConfig = {
  excludeKeys: ['group-video', 'insertVideo', 'uploadVideo'],
  modalAppendToBody: true
};

const editorConfig = {
  placeholder: '记录这个层级的环境、实体、入口、出口和生存规则...',
  MENU_CONF: {
    uploadImage: {
      allowedFileTypes: ['image/*'],
      maxFileSize: 8 * 1024 * 1024,
      customUpload: (file: File, insertFn: (src: string, alt: string, href: string) => void) => {
        if (!file.type.startsWith('image/')) {
          ElMessage.warning('请选择图片文件');
          return;
        }
        editorImageUploading.value = true;
        uploadImgBlob({
          url: 'file/wangeditor/uploadImg',
          file,
          data: { fileName: file.name },
          loading: { open: false, text: '' },
          success: (res: any) => {
            editorImageUploading.value = false;
            if (res.code !== 0) {
              ElMessage.error(res.msg || '正文图片上传失败');
              return;
            }
            const uploadedUrl = getUploadedImageUrl(res);
            if (!uploadedUrl) {
              ElMessage.warning('图片已上传，但未获取到图片地址');
              return;
            }
            insertFn(uploadedUrl, file.name, '');
            ElMessage.success('正文图片已插入');
          },
          error: () => {
            editorImageUploading.value = false;
            ElMessage.error('正文图片上传失败，请稍后重试');
          }
        });
      }
    }
  }
};

type EditorImageWidthRatio = {
  src: string;
  alt: string;
  href: string;
  width: string;
};

const getUploadedImageUrl = (res: any) => {
  const data = res?.data;
  if (typeof data === 'string') return normalizeUploadAssetUrl(data);
  return normalizeUploadAssetUrl(data?.url || data?.src || data?.path || '');
};

const handleEditorCreated = (editor: any) => {
  editorRef.value = editor;
  editor.on('fullScreen', () => setEditorFullscreenState(true));
  editor.on('unFullScreen', () => setEditorFullscreenState(false));
  nextTick(() => {
    resetImageResizeObserver();
    syncLivePreviewFromEditor();
  });
};

const handleEditorChange = (editor: any) => {
  emit('update:previewHtml', getPreviewHtmlFromEditor(editor));
};

const getEditorRoot = () => {
  return richEditorRef.value?.querySelector<HTMLElement>('[data-slate-editor]') || null;
};

const resetImageResizeObserver = () => {
  imageResizeObserver?.disconnect();
  imageResizeObserver = null;
  const editorRoot = getEditorRoot();
  if (!editorRoot || typeof MutationObserver === 'undefined') return;

  imageResizeObserver = new MutationObserver((mutations) => {
    const shouldSync = mutations.some((mutation) => {
      if (mutation.type === 'childList') return true;
      const target = mutation.target as HTMLElement;
      return target.classList?.contains('w-e-image-container') && mutation.attributeName === 'style';
    });
    if (shouldSync) scheduleLivePreviewSync();
  });

  imageResizeObserver.observe(editorRoot, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['style']
  });
};

const scheduleLivePreviewSync = () => {
  if (typeof window === 'undefined') return;
  if (previewSyncFrame) window.cancelAnimationFrame(previewSyncFrame);
  previewSyncFrame = window.requestAnimationFrame(() => {
    previewSyncFrame = 0;
    syncLivePreviewFromEditor();
  });
};

const syncLivePreviewFromEditor = () => {
  emit('update:previewHtml', getPreviewHtmlFromEditor(editorRef.value));
};

const syncHtmlBeforeSave = () => {
  const html = getPreviewHtmlFromEditor(editorRef.value);
  emit('update:html', html);
  emit('update:previewHtml', html);
};

const getEditorImageWidthRatios = (): EditorImageWidthRatio[] => {
  const editorRoot = getEditorRoot();
  if (!editorRoot) return [];
  const containers = Array.from(editorRoot.querySelectorAll<HTMLElement>('.w-e-image-container'));

  return containers
    .map((container) => {
      const image = container.querySelector<HTMLImageElement>('img');
      if (!image) return null;
      return {
        src: image.getAttribute('src') || '',
        alt: image.getAttribute('alt') || '',
        href: image.getAttribute('data-href') || '',
        width: getImageWidthRatioStyle(container)
      };
    })
    .filter((item): item is EditorImageWidthRatio => !!item && !!item.src);
};

const getImageWidthRatioStyle = (container: HTMLElement) => {
  const styleWidth = container.style.width.trim();
  if (styleWidth.endsWith('%')) return styleWidth;

  const editorWidth = getEditorContentWidth();
  const imageWidth = container.getBoundingClientRect().width;
  if (!editorWidth || !imageWidth) return styleWidth;

  const percent = Math.max(1, Math.min(100, (imageWidth / editorWidth) * 100));
  return `${formatPercent(percent)}%`;
};

const getEditorContentWidth = () => {
  const editorRoot = getEditorRoot();
  if (!editorRoot) return 0;
  const style = window.getComputedStyle(editorRoot);
  const paddingX = parseFloat(style.paddingLeft || '0') + parseFloat(style.paddingRight || '0');
  return Math.max(0, editorRoot.clientWidth - paddingX);
};

const formatPercent = (value: number) => {
  return value.toFixed(2).replace(/\.?0+$/, '');
};

const getPreviewHtmlFromEditor = (editor: any) => {
  const html = editor?.getHtml?.() || props.html || '';
  const htmlWithTables = restoreEditorTablesIfNeeded(html);
  return applyEditorImageWidthRatioToHtml(normalizeUploadAssetHtml(htmlWithTables));
};

const restoreEditorTablesIfNeeded = (html: string) => {
  const editorRoot = getEditorRoot();
  if (!editorRoot?.querySelector('table')) return html;

  const htmlTableCount = (html.match(/<table\b/gi) || []).length;
  const editorTableCount = editorRoot.querySelectorAll('table').length;
  if (htmlTableCount >= editorTableCount) return html;

  return serializeEditorDomForPreview() || html;
};

const serializeEditorDomForPreview = () => {
  const editorRoot = getEditorRoot();
  if (!editorRoot) return '';

  const clone = editorRoot.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('.w-e-image-dragger, [data-slate-zero-width]').forEach((element) => element.remove());
  clone.querySelectorAll('.table-container').forEach((container) => {
    const table = container.querySelector('table');
    if (table) container.replaceWith(table);
  });

  cleanEditorPreviewNode(clone);
  unwrapPlainSpans(clone);
  return clone.innerHTML;
};

const cleanEditorPreviewNode = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (attribute.name.startsWith('data-slate') || attribute.name === 'contenteditable' || attribute.name === 'spellcheck') {
        element.removeAttribute(attribute.name);
      }
    });
  });
};

const unwrapPlainSpans = (root: HTMLElement) => {
  Array.from(root.querySelectorAll('span')).forEach((span) => {
    if (span.attributes.length > 0) return;
    span.replaceWith(...Array.from(span.childNodes));
  });
};

const applyEditorImageWidthRatioToHtml = (html: string) => {
  if (!html) return '';
  const editorImages = getEditorImageWidthRatios();
  if (editorImages.length === 0 && !/<table/i.test(html)) return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  applyTablePreviewStyles(template.content);
  const previewImages = Array.from(template.content.querySelectorAll<HTMLImageElement>('img'));
  const usedIndexes = new Set<number>();

  previewImages.forEach((image, imageIndex) => {
    const matchedIndex = findEditorImageIndex(image, editorImages, usedIndexes, imageIndex);
    if (matchedIndex < 0) return;
    const matchedImage = editorImages[matchedIndex];
    usedIndexes.add(matchedIndex);

    const imageContainer = image.closest('.w-e-image-container') as HTMLElement | null;
    const target = imageContainer || image;
    if (matchedImage.width) target.style.width = matchedImage.width;
    else target.style.removeProperty('width');

    target.style.removeProperty('height');
    if (imageContainer) {
      image.style.width = '100%';
      image.style.height = 'auto';
    } else {
      image.style.removeProperty('height');
    }
  });

  return template.innerHTML;
};

const applyTablePreviewStyles = (root: DocumentFragment) => {
  root.querySelectorAll<HTMLTableElement>('table').forEach((table) => {
    mergeInlineStyles(table, {
      maxWidth: '100%',
      margin: '14px 0',
      borderCollapse: 'collapse',
      border: '1px solid rgba(0, 240, 216, 0.34)',
      background: 'rgba(5, 10, 22, 0.36)'
    });
  });

  root.querySelectorAll<HTMLTableCellElement>('th, td').forEach((cell) => {
    mergeInlineStyles(cell, {
      minWidth: '44px',
      padding: '8px 10px',
      border: '1px solid rgba(0, 240, 216, 0.34)',
      lineHeight: '1.55',
      textAlign: 'left',
      verticalAlign: 'top'
    });

    if (!cell.textContent?.trim() && cell.children.length === 0) {
      cell.innerHTML = '&nbsp;';
    }
  });

  root.querySelectorAll<HTMLTableCellElement>('th').forEach((cell) => {
    mergeInlineStyles(cell, {
      color: '#dffefa',
      fontWeight: '800',
      background: 'rgba(0, 240, 216, 0.12)'
    });
  });
};

const mergeInlineStyles = (element: HTMLElement, styles: Partial<CSSStyleDeclaration>) => {
  Object.entries(styles).forEach(([key, value]) => {
    if (value) element.style[key as any] = value;
  });
};

const findEditorImageIndex = (image: HTMLImageElement, editorImages: EditorImageWidthRatio[], usedIndexes: Set<number>, fallbackIndex: number) => {
  const src = image.getAttribute('src') || '';
  const alt = image.getAttribute('alt') || '';
  const href = image.getAttribute('data-href') || '';
  const matchedIndex = editorImages.findIndex((item, index) => {
    return !usedIndexes.has(index) && item.src === src && item.alt === alt && item.href === href;
  });

  if (matchedIndex >= 0) return matchedIndex;
  if (!usedIndexes.has(fallbackIndex) && editorImages[fallbackIndex]) return fallbackIndex;
  return editorImages.findIndex((_, index) => !usedIndexes.has(index));
};

const handleEditorAlert = (info: string, type: 'success' | 'info' | 'warning' | 'error') => {
  ElMessage[type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'info'](info);
};

const setEditorFullscreenState = (value: boolean) => {
  document.body.classList.toggle('level-editor-fullscreen-active', value);
};

const destroyEditor = () => {
  imageResizeObserver?.disconnect();
  imageResizeObserver = null;
  if (previewSyncFrame) {
    window.cancelAnimationFrame(previewSyncFrame);
    previewSyncFrame = 0;
  }
  if (editorRef.value) {
    editorRef.value.destroy();
    editorRef.value = null;
  }
};

watch(
  () => props.html,
  () => {
    nextTick(() => {
      resetImageResizeObserver();
      syncLivePreviewFromEditor();
    });
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  setEditorFullscreenState(false);
  destroyEditor();
});

defineExpose({
  syncHtmlBeforeSave,
  isImageUploading: editorImageUploading
});
</script>
